import { useTranslation } from "@sagot/i18n";
import { Icon, type IconName } from "@sagot/ui";
import { useFormatters, type CustomDomainOut } from "../../shared";

type StepKey = "dns" | "certificate" | "live";
type StepState = CustomDomainOut["progress"]["dns"]["state"];

const STEPS: readonly StepKey[] = ["dns", "certificate", "live"];

const STATE_UI: Readonly<Record<StepState, { icon: IconName; tone: string }>> = {
  done: { icon: "check-circle", tone: "bg-success-soft text-fg" },
  pending: { icon: "clock", tone: "bg-warning-soft text-fg" },
  failed: { icon: "x-circle", tone: "bg-danger-soft text-fg" },
};

function StepChip({ label, state, detail, stepId }: { label: string; state: StepState; detail: string; stepId: string }) {
  const { t } = useTranslation();
  const ui = STATE_UI[state];
  return (
    <li className={`flex min-w-0 items-start gap-2 rounded-md px-3 py-2 ${ui.tone}`} data-testid={`domain-step-${stepId}`} data-state={state}>
      <Icon name={ui.icon} className="mt-0.5 size-4 shrink-0" />
      <span className="min-w-0">
        <span className="block text-xs font-semibold">
          {label}
          <span className="sr-only">: {t(`settings.domains.progress.state.${state}`)}</span>
        </span>
        <span className="block text-[11px] text-fg-muted">{detail}</span>
      </span>
    </li>
  );
}

/**
 * Domain added → DNS found → certificate issued → live, each with its state and when it was
 * reached (or, while it is not, when we last looked) (mockup domains pin 3, HCU-004).
 */
export function DomainProgress({ domain }: { domain: CustomDomainOut }) {
  const { t } = useTranslation();
  const format = useFormatters();
  const detail = (key: StepKey): string => {
    const step = domain.progress[key];
    if (step.at) return t("settings.domains.progress.reached", { when: format.relative(step.at) });
    if (step.state === "failed") return t("settings.domains.progress.failedHint");
    return domain.last_checked_at ? t("settings.domains.progress.checked", { when: format.relative(domain.last_checked_at) }) : t("settings.domains.progress.notChecked");
  };
  return (
    <ol className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4" aria-label={t("settings.domains.progress.label", { host: domain.hostname })} data-testid="domain-progress">
      <StepChip stepId="added" label={t("settings.domains.progress.added")} state="done" detail={t("settings.domains.progress.reached", { when: format.relative(domain.created_at) })} />
      {STEPS.map((key) => (
        <StepChip key={key} stepId={key} label={t(`settings.domains.progress.${key}`)} state={domain.progress[key].state} detail={detail(key)} />
      ))}
    </ol>
  );
}
